import {getTestProps} from '@benchling/js-test-utils';
import {TextInput} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY, BLUE_POWDER, BLUE} from 'ui-library/Colors';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as Net from 'util/Net';

interface OnboardingQuestion {
  question: string;
  choices: Array<string>;
}

interface OnboardingAppState {
  is_complete?: boolean;
  questions_asked?: Array<OnboardingQuestion>;
}

function isOnboardingAppState(state: unknown): state is OnboardingAppState {
  if (!state || typeof state !== 'object') {
    return false;
  }
  const s = state as OnboardingAppState;
  return s.questions_asked != null || s.is_complete != null;
}

function FreeTextChoiceButton({
  value,
  onChange,
  onDiscard,
}: {
  value: string;
  onChange: (value: string) => void;
  onDiscard: () => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    setIsOpen(true);
    inputRef.current?.focus();
  }, []);

  const handleBlur = useCallback(() => {
    if (!value.trim()) {
      setIsOpen(false);
      onDiscard();
    }
  }, [value, onDiscard]);

  const isSelected = value.trim().length > 0;

  if (isOpen) {
    return (
      <div
        className={css(
          styles.freeTextButton,
          styles.freeTextButtonOpen,
          isSelected && styles.freeTextButtonSelected
        )}
        onClick={() => inputRef.current?.focus()}
        {...getTestProps({component: 'AIOnboardingScreen', element: 'something-else-button'})}
      >
        <span style={{paddingRight: 8, display: 'inline-flex', alignItems: 'center'}}>✏</span>
        <TextInput
          ref={inputRef}
          autoFocus={true}
          variant="unstyled"
          classNames={{input: css(styles.freeTextInput)}}
          styles={{wrapper: {display: 'contents'}}}
          placeholder="Something else"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onBlur={handleBlur}
        />
      </div>
    );
  }

  return (
    <Button
      aria-pressed={isSelected}
      buttonStyle={isSelected ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_WHITE}
      size={ButtonSize.MEDIUM}
      className={css(styles.freeTextButton)}
      icon={<span style={{paddingRight: 8, display: 'inline-flex', alignItems: 'center'}}>✏</span>}
      text="Something else"
      onClick={handleClick}
      dataTestAttrs={{component: 'AIOnboardingScreen', element: 'something-else-button'}}
    />
  );
}
const SKIPPED_ANSWER = '[skipped]';

function QuestionView({
  chatSessionId,
  questions,
}: {
  chatSessionId: string;
  questions: Array<OnboardingQuestion>;
}): JSX.Element {
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchAnswers, setBatchAnswers] = useState<Array<string>>([]);
  const [selectedChoices, setSelectedChoices] = useState<Set<string>>(new Set());
  const [freeTextValue, setFreeTextValue] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[batchIndex] ?? null;
  const freeTextTrimmed = freeTextValue.trim();
  const effectiveAnswer = [...selectedChoices, ...(freeTextTrimmed ? [freeTextTrimmed] : [])].join(', ');

  const canContinue = !isSubmitting && effectiveAnswer.length > 0;

  const handleSelectChoice = useCallback((choice: string) => {
    setSelectedChoices((prev) => {
      const next = new Set(prev);
      if (next.has(choice)) {
        next.delete(choice);
      } else {
        next.add(choice);
      }
      return next;
    });
  }, []);

  const handleDiscardFreeText = useCallback(() => {
    setFreeTextValue('');
  }, []);

  const submitAnswers = useCallback(
    async (answers: Array<string>) => {
      setIsSubmitting(true);
      try {
        const messageText = questions.map((q, i) => `${q.question}: ${answers[i] ?? ''}`).join('\n');
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId,
          agentType: AgentType.ONBOARDING,
          messageBlocks: [{type: 'text', text: messageText}],
          userChatContext: ChatDAO.getUserChatContext({contextIds: []}),
          sources: [],
        });
      } catch (err) {
        if (!Net.isServerRequestError(err)) {
          throw err;
        }
        setIsSubmitting(false);
      }
    },
    [chatSessionId, questions]
  );

  const handleAdvance = useCallback(
    async (answer: string) => {
      const updatedAnswers = [...batchAnswers, answer];
      const nextIndex = batchIndex + 1;

      if (nextIndex < questions.length) {
        setBatchAnswers(updatedAnswers);
        setBatchIndex(nextIndex);
        setSelectedChoices(new Set());
        setFreeTextValue('');
        return;
      }

      await submitAnswers(updatedAnswers);
    },
    [batchAnswers, batchIndex, questions, submitAnswers]
  );

  if (!currentQuestion) {
    return <></>;
  }

  return (
    <div className={css(styles.body)}>
      <h2 className={css(styles.questionHeading)}>{currentQuestion.question}</h2>
      <div
        className={css(styles.choicesGrid)}
        {...getTestProps({component: 'AIOnboardingScreen', element: 'choices'})}
      >
        {currentQuestion.choices.map((choice) => (
          <Button
            key={choice}
            aria-pressed={selectedChoices.has(choice)}
            buttonStyle={
              selectedChoices.has(choice) ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_WHITE
            }
            size={ButtonSize.MEDIUM}
            growToFitContents={true}
            text={choice}
            onClick={() => {
              handleSelectChoice(choice);
            }}
          />
        ))}
        <FreeTextChoiceButton
          key={currentQuestion.question}
          value={freeTextValue}
          onChange={setFreeTextValue}
          onDiscard={handleDiscardFreeText}
        />
      </div>
      <div className={css(styles.continueRow)}>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text="Continue"
          onClick={() => effectiveAnswer && handleAdvance(effectiveAnswer)}
          disabled={!canContinue}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'continue-button'}}
        />
        <Button
          buttonStyle={ButtonStyle.TEXT}
          size={ButtonSize.SMALL}
          text="Skip"
          onClick={() => handleAdvance(SKIPPED_ANSWER)}
          disabled={isSubmitting}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'skip-button'}}
        />
      </div>
    </div>
  );
}

/**
 * Onboarding screen driven directly by useMessageThread.
 *
 * Thread status drives all UI states:
 *  - isAssistantRunning (QUEUED/ACTIVE) → loading spinner
 *  - isAwaitingUserInput (AWAITING_USER_INPUT) → question UI sourced from latestApplicationState
 *  - errorMessage → error + skip UI
 */
export function AIOnboardingScreen({
  chatSessionId,
  onDismiss,
}: {
  chatSessionId: string;
  onDismiss: () => void;
}): JSX.Element {
  const {
    isLoading,
    isAssistantRunning,
    isAwaitingUserInput,
    isCompleted,
    latestApplicationState,
    errorMessage,
  } = useMessageThread({chatSessionId});

  const appState = isOnboardingAppState(latestApplicationState) ? latestApplicationState : null;

  // When the thread completes, transition to the settings page automatically.
  useEffect(() => {
    if (isCompleted) {
      onDismiss();
    }
  }, [isCompleted, onDismiss]);

  // Questions are available in app state while AWAITING_USER_INPUT.
  const currentQuestions = useMemo((): Array<OnboardingQuestion> | null => {
    if (!isAwaitingUserInput || !appState?.questions_asked?.length) {
      return null;
    }
    return appState.questions_asked;
  }, [isAwaitingUserInput, appState]);

  const showLoading = isLoading || isAssistantRunning || isCompleted;
  const hasPreviousQuestions = (appState?.questions_asked?.length ?? 0) > 0;
  const loadingMessage = hasPreviousQuestions
    ? 'Processing your answers…'
    : 'Starting onboarding and gathering information…';

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'AIOnboardingScreen', element: 'root'})}
    >
      <div className={css(styles.exitRow)}>
        <Button
          aria-label="Exit onboarding"
          buttonStyle={ButtonStyle.TRANSPARENT}
          size={ButtonSize.SMALL}
          icon="close"
          onClick={onDismiss}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'exit-button'}}
        />
      </div>

      {showLoading && (
        <div className={css(styles.body)}>
          <div className={css(styles.loadingState)}>
            <CenteredSpinner />
            <p className={css(styles.loadingText)}>{loadingMessage}</p>
          </div>
        </div>
      )}

      {!showLoading && errorMessage && (
        <div className={css(styles.body)}>
          <div className={css(styles.errorState)}>
            <p className={css(styles.errorText)}>{errorMessage}</p>
            <Button
              buttonStyle={ButtonStyle.TEXT}
              size={ButtonSize.SMALL}
              text="Exit"
              onClick={onDismiss}
              dataTestAttrs={{component: 'AIOnboardingScreen', element: 'error-exit-button'}}
            />
          </div>
        </div>
      )}

      {!showLoading && !errorMessage && isAwaitingUserInput && currentQuestions && (
        <QuestionView chatSessionId={chatSessionId} questions={currentQuestions} />
      )}
    </div>
  );
}

const PADDING = 16;

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 650 + PADDING * 2,
    width: '100%',
    margin: '0 auto',
    padding: `8px ${PADDING}px 16px`,
  },
  exitRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '4px',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
  },
  questionHeading: {
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '24px',
    margin: '0 0 8px',
  },
  choicesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  freeTextButton: {
    borderStyle: 'dashed',
  },
  freeTextButtonOpen: {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px dashed ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '5px 12px',
    cursor: 'text',
  },
  freeTextButtonSelected: {
    background: BLUE_POWDER,
    borderColor: BLUE_POWDER,
    color: BLUE,
  },
  freeTextInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '13px',
    width: '160px',
    padding: 0,
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  loadingText: {
    fontSize: '13px',
    color: GRAY,
    textAlign: 'center',
    margin: 0,
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  errorText: {
    fontSize: '13px',
    color: GRAY,
    textAlign: 'center',
    margin: 0,
  },
  continueRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '16px',
  },
});
